import React from 'react';

import cn from 'classnames';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import { HiChevronLeft } from 'react-icons/hi';

import PageTitle from '../components/PageTitle';
import ScrollToTop from '../components/ScrollToTop';
import { Button } from '../input';

const headerTitle = (list) =>
  list?.map((item, i) => {
    const index = i;
    return (
      <div
        key={`title-${item}-${index}`}
        className={cn('inline space-x-3', {
          'last:text-primary-500': list?.length > 1,
        })}
      >
        <span>{item}</span>
        {i !== list?.length - 1 && <span className="font-black inline">-</span>}
      </div>
    );
  });

export const PageHeader = ({
  title,
  badge,
  goBack,
  preTitle,
  subTitle,
  isLoading,
  withTitle,
  tabPreTitle,
  description,
  subPreTitle,
  withTabTitle,
  withSubTitle,
  titleClassName,
  withButtonAction,
  withTitleContainer,
  containerClassName,
  buttonActionClassName,
}) => {
  const history = useHistory();

  const navigation = React.useMemo(() => {
    if (typeof goBack === 'string') return { to: goBack };
    if (typeof goBack === 'function') {
      return {
        onClick: (e) => {
          e.preventDefault();
          goBack();
        },
      };
    }
    return () => history?.goBack();
  }, [goBack, history]);

  const renderTitle = React.useMemo(() => {
    if (Array.isArray(title)) {
      const desktop = title?.flat()?.filter((x) => x && x !== '');
      const mobile = [title[0], title[title.length - 1]]?.filter((x) => x);

      return (
        <div className="flex">
          <div className="hidden md:block truncate last:truncate space-x-3">
            {headerTitle(desktop)}
          </div>
          <div className="block md:hidden truncate last:truncate space-x-3">
            {headerTitle(mobile)}
          </div>
        </div>
      );
    }
    return title;
  }, [title]);

  const renderTabTitle = React.useMemo(() => {
    if (typeof title === 'string') {
      return title;
    }
    if (Array.isArray(title) && title) {
      const $title = title?.flat()?.filter((x) => x && x !== '');
      if ($title.length > 1) {
        return $title.toString().replaceAll(',', ' - ');
      }
      return $title;
    }
    return '';
  }, [title]);

  return (
    <>
      {withTabTitle && (
        <PageTitle
          title={`${tabPreTitle} ${
            title && !isLoading ? `| ${renderTabTitle}` : ''
          }`}
        />
      )}

      {withTitleContainer && (
        <div
          className={cn(
            'flex flex-col md:flex-row items-start md:items-center space-y-3 md:space-y-0 container mx-auto min-h-[46px]',
            containerClassName
          )}
        >
          {withTitle && (
            <div
              className={cn(
                'max-w-full w-full overflow-hidden',
                titleClassName
              )}
            >
              <div className="flex items-center space-x-3 leading-none">
                {goBack && (
                  <Button
                    className="btn outline-light sm w-9 h-9"
                    label={
                      <span>
                        <HiChevronLeft className="text-secondary-500 w-8 h-8" />
                      </span>
                    }
                    {...navigation}
                  />
                )}
                <div
                  className={cn(
                    'min-h-[36px] truncate flex items-start flex-col justify-start',
                    {
                      invisible: isLoading,
                    },
                    {
                      hidden: !goBack && !title,
                    }
                  )}
                >
                  {preTitle && (
                    <div className="text-xs text-gray-400 tracking-wide font-semibold">
                      {preTitle}
                    </div>
                  )}
                  {title && (
                    <div className="flex flex-col md:flex-row flex-1 items-start md:items-center md:space-x-3 mt-1">
                      <div className="text-2xl font-bold truncate xs:w-52 w-72 md:w-full md:max-w-screen-sm lg:max-w-screen-md">
                        {renderTitle}
                      </div>
                      {badge && badge}
                    </div>
                  )}
                  {subPreTitle && (
                    <div className="font-medium text-sm mt-px">
                      {subPreTitle}
                    </div>
                  )}
                </div>
              </div>
              {withSubTitle && (
                <div
                  className={cn('space-y-1', {
                    'mt-4': goBack && title,
                  })}
                >
                  {subTitle && (
                    <div className="font-semibold text-lg leading-tight">
                      {subTitle}
                    </div>
                  )}
                  {description && (
                    <div className="font-normal text-sm w-full lg:w-9/12">
                      {description}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
          {withButtonAction && (
            <div
              className={cn(
                'flex gap-3 w-full md:w-auto md:ml-auto',
                buttonActionClassName
              )}
            >
              {withButtonAction}
            </div>
          )}
        </div>
      )}
    </>
  );
};

PageHeader.defaultProps = {
  subTitle: '',
  title: false,
  badge: false,
  goBack: false,
  preTitle: false,
  withTitle: true,
  description: '',
  isLoading: false,
  withTabTitle: true,
  subPreTitle: false,
  withSubTitle: false,
  titleClassName: false,
  withButtonAction: false,
  withTitleContainer: true,
  containerClassName: false,
  buttonActionClassName: false,
  tabPreTitle: process.env.REACT_APP_TITLE,
};

PageHeader.propTypes = {
  withTabTitle: PropTypes.bool,
  title: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.string,
    PropTypes.element,
    PropTypes.instanceOf(Array),
  ]),
  isLoading: PropTypes.bool,
  badge: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.element,
    PropTypes.instanceOf(Object),
  ]),
  withTitle: PropTypes.bool,
  subPreTitle: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  containerClassName: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  buttonActionClassName: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.bool,
  ]),
  tabPreTitle: PropTypes.string,
  withSubTitle: PropTypes.bool,
  goBack: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.string,
    PropTypes.instanceOf(Function),
  ]),
  withButtonAction: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.element.isRequired,
    PropTypes.instanceOf(Object).isRequired,
  ]),
  withTitleContainer: PropTypes.bool,
  preTitle: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  subTitle: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  description: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  titleClassName: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
};
function PageWrapper({
  children,
  withScroll,
  withScrollToTop,
  className,
  containerClassName,
  scrollToTopProps,
}) {
  const ref = React.useRef();

  return (
    <div className={cn('page-outer-wrapper', containerClassName)}>
      <div className={cn('page-inner-wrapper', className)} ref={ref}>
        <div
          className={cn('page-wrapper', {
            'md:overflow-hidden': !withScroll,
          })}
        >
          {children}
        </div>
        {withScrollToTop && false && (
          <ScrollToTop {...scrollToTopProps} el={ref.current} />
        )}
      </div>
    </div>
  );
}

PageWrapper.defaultProps = {
  children: <></>,
  className: false,
  withScroll: false,
  scrollToTopProps: {},
  withScrollToTop: false,
  containerClassName: false,
};

PageWrapper.propTypes = {
  withScroll: PropTypes.bool,
  withScrollToTop: PropTypes.bool,
  children: PropTypes.instanceOf(Object),
  scrollToTopProps: PropTypes.instanceOf(Object),
  className: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  containerClassName: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
};

export default PageWrapper;
